import { defineSlotRecipe } from '@chakra-ui/react'

export const pageRecipe = defineSlotRecipe({
  className: 'page',
  slots: [
    'root',
    'breadcrumb',
    'header',
    'headingRow',
    'titleGroup',
    'content',
    'section',
    'overviewGrid',
    'navigationColumns',
    'contextColumns',
    'navigation',
    'context',
    'main',
  ],
  // Each slot is rendered by the component of the same name in
  // src/components/layout/Page.tsx. The comments say what a slot contains, so the
  // spacing can be judged here without opening that file.
  base: {
    // <Page.Root> — the page as a whole: header, then content.
    // It owns the rhythm between them, rather than each block pushing itself away
    // with its own bottom margin. Changing the page rhythm is one edit here.
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'page',
      width: 'full',
    },
    // <Page.Header> — breadcrumb, back button, and title group stacked.
    // They belong together, so they sit in the header rather than floating above
    // it as separate page blocks.
    header: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 'stack',
    },
    // The breadcrumb trail above the back button.
    breadcrumb: {},
    // Title and its add-on (a badge, for example) side by side on one line.
    headingRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 'inline',
      flexWrap: 'wrap',
    },
    // The heading row plus the meta line below it, for example a date.
    // Same gap as the header, so the meta line keeps the header's rhythm.
    titleGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'stack',
    },
    // <Page.Content> — everything below the header.
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'page',
      minWidth: '0',
    },
    // <Page.Section> — one block of content, its children stacked.
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'stack',
      width: 'full',
    },
    // <Page.OverviewGrid> — a responsive grid of cards on an overview page.
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: {
        base: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
        lg: 'repeat(3, minmax(0, 1fr))',
      },
      gap: 'section',
    },
    // <Page.NavigationColumns> — navigation left, main content right.
    navigationColumns: {
      display: 'grid',
      gridTemplateColumns: { base: '1fr', lg: 'minmax(0, 360px) minmax(0, 1fr)' },
      columnGap: 'section',
      rowGap: 'section',
      alignItems: 'start',
    },
    // <Page.ContextColumns> — main content left, contextual information right.
    contextColumns: {
      display: 'grid',
      gridTemplateColumns: { base: '1fr', lg: 'minmax(0, 1fr) minmax(0, 360px)' },
      columnGap: 'section',
      rowGap: 'section',
      alignItems: 'start',
    },
    // <Page.Navigation> — the navigation column itself.
    navigation: {
      minWidth: '0',
    },
    // <Page.Context> — the contextual column. It stacks its blocks with the same
    // distance that separates the columns, so the grid reads the same in both
    // directions.
    context: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'section',
      minWidth: '0',
    },
    // <Page.Main> — the primary column, stacked like the contextual one.
    main: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'section',
      minWidth: '0',
    },
  },
  variants: {
    /**
     * Reserves room below the content for a floating action bar, so the end of
     * the page stays reachable instead of sitting underneath it.
     */
    hasActionBar: {
      true: {
        content: {
          paddingBlockEnd: '24',
        },
      },
    },
  },
})
